/**
 * v-entitlement Directive
 *
 * Conditionally shows/hides elements based on entitlements.
 *
 * Usage:
 *   v-entitlement="'PATIENT_MANAGEMENT'"           — single key
 *   v-entitlement="['FEED_MANAGEMENT', 'feed.order_full']"  — ALL required
 *   v-entitlement:any="['INVENTORY', 'MILK_MANAGEMENT']"    — ANY required
 *
 * The element is hidden via `display: none` when the entitlement check fails.
 * The directive subscribes to the entitlements store and reacts to changes
 * (e.g. when entitlements finish loading after login).
 */
import type { Directive } from 'vue';
import { watch } from 'vue';
import { useEntitlementsStore } from '@/stores';

const teardownMap = new WeakMap<HTMLElement, () => void>();

export const vEntitlement: Directive<HTMLElement, string | string[]> = {
  mounted(el, binding) {
    const store = useEntitlementsStore();

    updateVisibility(el, binding.value, binding.arg, store);

    // Subscribe to store changes so visibility updates when entitlements load/change
    const unwatch = watch(
      () => store.entitlements,
      () => updateVisibility(el, binding.value, binding.arg, store),
      { deep: true },
    );
    teardownMap.set(el, unwatch);
  },
  updated(el, binding) {
    const store = useEntitlementsStore();
    updateVisibility(el, binding.value, binding.arg, store);
  },
  beforeUnmount(el) {
    const unwatch = teardownMap.get(el);
    if (unwatch) {
      unwatch();
      teardownMap.delete(el);
    }
  },
};

function updateVisibility(
  el: HTMLElement,
  value: string | string[],
  arg: string | undefined,
  store: ReturnType<typeof useEntitlementsStore>,
) {
  let granted: boolean;

  if (typeof value === 'string') {
    granted = store.hasEntitlement(value);
  } else if (arg === 'any') {
    granted = store.hasAnyEntitlement(value);
  } else {
    // Default: all required
    granted = store.hasAllEntitlements(value);
  }

  el.style.display = granted ? '' : 'none';
}
